import { z } from 'zod';
import { E164, TimestampSchema } from '../primitives.js';
/**
 * Per-business client base (doc 11). A returning caller is recognised by their
 * phone number: the record carries the confirmed name plus a bounded, most-
 * recent-first service history the AI can suggest from ("the usual manicure?").
 *
 * Maintained by the booking transaction (doc 11 §6.2, ADR-0007) — every
 * successful booking upserts the client — plus manual owner CRUD in the portal.
 * Stats fields (`bookingsCount`, `recentServices`, `lastSeenAt`) are SERVER-ONLY
 * (doc 11 D4): the AI repeats them to callers, so they must stay trustworthy.
 */
/** One entry of the bounded per-client service history (doc 11 D3). */
export const ClientServiceUseSchema = z.object({
    serviceId: z.string().min(1),
    /** Denormalised for prompt/display without a services join. */
    serviceName: z.string().min(1),
    /** Bookings of this service (made, not necessarily attended). */
    count: z.number().int().min(1),
    lastBookedAt: TimestampSchema,
});
/** Max distinct services kept on a client record (doc 11 D3). */
export const MAX_RECENT_SERVICES = 5;
/**
 * Consent to be messaged about an appointment (doc 16 §3, doc 21 §5).
 *
 * Scoped to ONE business: consent given to the salon is not consent given to
 * the owner's other business (doc 11 §3 controllership).
 *
 * Note what this does and does not gate. Booking, move and cancellation
 * messages are **transactional** — performance of the service the client just
 * asked for — and need no opt-in; they are suppressed only by `revokedAt`.
 * Reminders are business-initiated and send ONLY on `granted`.
 */
export const ReminderConsentSchema = z.object({
    /** `unknown` means NO SEND for reminders. Silence is not consent, and a
     * client base backfilled from history starts here and stays here until
     * somebody actually asks. */
    status: z.enum(['granted', 'denied', 'revoked', 'unknown']).default('unknown'),
    at: TimestampSchema.optional(),
    /** How we obtained it — auditable, and different sources carry different weight. */
    source: z.enum(['phone_call', 'whatsapp_conversation', 'portal_manual']).optional(),
    /** The exact wording the customer answered, for the GDPR record. */
    prompt: z.string().max(300).default(''),
    /** Set on opt-out. Permanent, immediate, and suppresses EVERYTHING including
     * transactional messages — no expiry, and no re-asking on the next booking.
     * Only an explicit opt-in in a later conversation can clear it. */
    revokedAt: TimestampSchema.nullable().default(null),
});
/** `businesses/{businessId}/clients/{e164}` — the doc id IS the E.164 number. */
export const ClientSchema = z.object({
    /** Mirrors the doc id, so queries/exports never need to parse ids. */
    phone: E164,
    name: z.string().min(1).max(120),
    /** Owner's free-text notes; never injected into the AI prompt (doc 11 §9). */
    notes: z.string().max(2000).default(''),
    /** ── Server-maintained stats (writes rejected client-side, doc 11 §5) ── */
    bookingsCount: z.number().int().min(0).default(0),
    /** Most-recent-first, distinct by serviceId, capped at {@link MAX_RECENT_SERVICES}. */
    recentServices: z.array(ClientServiceUseSchema).max(MAX_RECENT_SERVICES).default([]),
    /** Last successful booking touch; drives the portal's default (recency) sort. */
    lastSeenAt: TimestampSchema.optional(),
    /** Messaging consent (doc 16 §3). Defaulted, so every client record
     * written before this existed keeps parsing at `unknown` — i.e. no
     * reminders until somebody asks, which is the correct starting point. */
    reminders: ReminderConsentSchema.default({}),
    source: z.enum(['ai_call', 'manual']).default('ai_call'),
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema.optional(),
});
export const ClientWithIdSchema = ClientSchema.extend({ id: z.string() });
//# sourceMappingURL=client.js.map